import React, { useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Fab,
  Card,
  CardContent,
  Tooltip,
  Slide,
  IconButton,
  Chip,
  Stack,
  Paper,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  InputAdornment,
  Backdrop,
  Skeleton,
  AppBar,
  Toolbar,
  Container,
} from "@mui/material";

// Icons
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CreateIcon from '@mui/icons-material/Create';
import DownloadIcon from '@mui/icons-material/Download';
import CameraIcon from '@mui/icons-material/Camera';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoIcon from '@mui/icons-material/Info';
import FilterListIcon from '@mui/icons-material/FilterList';
import TodayIcon from '@mui/icons-material/Today';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ClearIcon from '@mui/icons-material/Clear';
import BusinessIcon from '@mui/icons-material/Business';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import RefreshIcon from '@mui/icons-material/Refresh';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EuroIcon from '@mui/icons-material/Euro';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PhoneIcon from '@mui/icons-material/Phone';
import WebIcon from '@mui/icons-material/Web';
import UpdateIcon from '@mui/icons-material/Update';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QRCode from "qrcode";

// GraphQL imports
import { Machine } from "../graphql/querys/machine";
import { Renting } from "../graphql/querys/Renting";
import { Items } from "../graphql/querys/items";
import { Client } from "../graphql/querys/client";
import RentingPopup from "../components/RentingPopup";
import { CloseRent } from "../graphql/mutation/stopRenting";
import { UpdateMachineItems } from "../graphql/mutation/updateMachineItems";
import { RemoveItemFromMachine } from "../graphql/mutation/deleteItemMachine";
import { GET_INTERVENTIONS_BY_MACHINE } from "../graphql/querys/interventionsByMachine";
import { UPDATE_DOC_ITEMS } from "../graphql/mutation/updateMachineDocuments";
import { UPDATE_DOC_PUBLIC } from "../graphql/mutation/updateMachineDocumentsPublic";
import { updateDocumentItemsRemove, UpdateDocumentItemsRemove } from "../graphql/mutation/removeMachine";
import { getErrorMessage, getFields } from "../helpers";
import ImageUpload from "../components/imageUpload";
import { RentingByCostumer } from "../graphql/querys/RentingByCustomer";
import PDFGeneratorService from "../components/PdfGenerator";
import { REMOVE_MACHINE } from "../graphql/mutation/removeMachines";
import { UPDATE_MACHINE } from "../graphql/mutation/updateMachineDocuments copy";
import { CancelRent } from "../graphql/mutation/cancelRenting";
import { EDIT_INTERVENTION_MUTATION } from "../graphql/mutation/editIntervention";
import PDFGeneratorServiceFn from "../components/PdfGenerator_rel";
import { LoadingButton } from "@mui/lab";
import { UpdateRentingEndDate } from "../graphql/mutation/updateRentingEndDate";
import { uploadInterventionPhotos } from "../services/intervention-photo-upload";
import { uploadMachineDocuments } from "../services/machine-document-upload";

// Helper functions
export function toReadableDate(date) {
  date = new Date(date);
  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

const pendingInterventionPhoto = (file) => ({
  file,
  preview: URL.createObjectURL(file),
});
const interventionPhotoSource = (photo) => photo?.preview || photo;
const interventionPhotoValue = (photo) => photo?.file || photo;
function convertBase64ToBlobUrl(value) {
  if (!value) return null;

  // Already a URL/path (new migrated format)
  if (
    value.startsWith('/uploads/') ||
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('blob:')
  ) {
    return value;
  }

  try {
    const [mimePart, dataPart] = value.startsWith('data:')
      ? value.split(',')
      : [null, value];

    const mimeType = mimePart?.match(/data:(.*?);/)?.[1] || 'application/octet-stream';
    const base64Data = dataPart.replace(/-/g, '+').replace(/_/g, '/');
    const paddedData = base64Data.padEnd(base64Data.length + (4 - (base64Data.length % 4)) % 4, '=');

    const byteCharacters = atob(paddedData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return URL.createObjectURL(new Blob([byteArray], { type: mimeType }));
  } catch (error) {
    console.error('Error converting document data to blob URL:', error);
    return null;
  }
}

function calculateProjectedIncome(dataRenting) {
  if (!dataRenting?.renting?.previsionEndingRent || !dataRenting?.renting?.startedRent) {
    return 0;
  }

  const previsionEndingRent = new Date(dataRenting.renting.previsionEndingRent);
  previsionEndingRent.setHours(23, 59, 59, 999);

  const createdAt = new Date(dataRenting.renting.startedRent);
  createdAt.setHours(0, 0, 0, 0);

  const pricePerDay = dataRenting?.renting?.pricePerDay || 0;

  const timeDiff = previsionEndingRent - createdAt;
  const daysDiff = timeDiff / (1000 * 3600 * 24);

  const projectedIncome = daysDiff * pricePerDay;

  return Math.round(projectedIncome);
}

function calculateLiveIncome(dataRenting) {
  if (!dataRenting?.renting?.startedRent) {
    return 0;
  }

  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);

  const createdAt = new Date(dataRenting.renting.startedRent);
  createdAt.setHours(0, 0, 0, 0);

  const pricePerDay = dataRenting?.renting?.pricePerDay || 0;

  const timeDiff = currentDate - createdAt;
  const daysDiff = timeDiff / (1000 * 3600 * 24);

  const liveIncome = daysDiff * pricePerDay;

  return Math.round(liveIncome);
}

// GraphQL Query
const RENTING_METRICS_QUERY = gql`
  query GetRentingMetrics($startDate: String, $endDate: String) {
    rentingMetrics(startDate: $startDate, endDate: $endDate)
  }
`;

export default function MachineScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  // Date state
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const startOfMonth = new Date(currentYear, currentMonth, 1);
  const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const formatDate = (date) => date?.toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(formatDate(startOfMonth));
  const [endDate, setEndDate] = useState(formatDate(endOfMonth));

  // UI state
  const [currentTab, setCurrentTab] = useState(0);
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [pdfPopup, setPdfPopup] = useState(false);
  const [PdfData, setPdfData] = useState();
  const [openRent, setIsOpenRent] = useState(false);
  const [activate, setActivate] = useState();
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsPopupOpen, setItemsPopupOpen] = useState(false);
  const [documentsPopupOpen, setDocumentsPopupOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editMachineDialogState, setEditMachineDialogState] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentIntervention, setCurrentIntervention] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [description, setDescription] = useState("");
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [selectedInterventions, setSelectedInterventions] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [dialogCancelRent, setDialogCancelRent] = useState(false);
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [currentDoc, setCurrentDoc] = useState("");
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [open1, setOpen1] = useState(false);
  const [newItemsWOEliminated, setNewItemsWOEliminated] = useState();
  const [inputs, setInputs] = useState({});
  const [image, setImage] = useState();
  const [updateEndDateDialogOpen, setUpdateEndDateDialogOpen] = useState(false);
  const [newEndDate, setNewEndDate] = useState(null);
  const [updateRentingEndDate] = useMutation(UpdateRentingEndDate);

  // Queries
  const { loading, error, data, refetch } = useQuery(Machine, {
    variables: { id },
  });
  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
  };
  const {
    loading: loadingRenting,
    error: errorRenting,
    data: dataRenting,
    refetch: refetchRenting,
  } = useQuery(Renting, { variables: { id } });

  const {
    data: dataRentingByClient,
    refetch: refetchDataRentingByClient
  } = useQuery(RentingByCostumer, {
    variables: { id: dataRenting?.renting?.client },
    skip: !dataRenting?.renting?.client
  });

  const {
    loading: loadingInterventions,
    error: errorInterventions,
    data: dataInterventions,
    refetch: refetchIntervention,
  } = useQuery(GET_INTERVENTIONS_BY_MACHINE, { variables: { id } });

  const {
    loading: loadingItems,
    data: dataItems,
    refetch: refetchItems,
  } = useQuery(Items, {
    variables: { term: searchTerm },
  });

  const { data: dataRentingMetrics } = useQuery(RENTING_METRICS_QUERY, {
    variables: { startDate, endDate },
  });

  const {
    data: clientData,
    loading: loadingClient
  } = useQuery(Client, {
    variables: { id: dataRenting?.renting?.client },
    skip: !dataRenting?.renting?.client
  });

  // Mutations
  const [closeRent] = useMutation(CloseRent);
  const [cancelRent] = useMutation(CancelRent);
  const [removeItem] = useMutation(RemoveItemFromMachine);
  const [updateMachineItems] = useMutation(UpdateMachineItems);
  const [updateDocumentItems] = useMutation(UPDATE_DOC_ITEMS);
  const [updateMachine] = useMutation(UPDATE_MACHINE);
  const [updateRemoveDocumentItems] = useMutation(updateDocumentItemsRemove);
  const [updateDocumentPublic] = useMutation(UPDATE_DOC_PUBLIC);
  const [removeMachine] = useMutation(REMOVE_MACHINE);
  const [editIntervention] = useMutation(EDIT_INTERVENTION_MUTATION);

  // Effects
  useEffect(() => {
    if (data && data.machine) {
      setInputs(data.machine);
    }
  }, [data]);

  useEffect(() => {
    if (dataItems && dataItems.items) {
      setItems(dataItems.items);
    }
  }, [dataItems]);

  useEffect(() => {
    refetchRenting();
    refetchDataRentingByClient();
    refetch();
    refetchItems();
  }, [pdfPopup]);

  useEffect(() => {
    if (openEditDialog === false) {
      refetchIntervention();
    }
  }, [openEditDialog]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
      refetchItems();
      refetchRenting();
      refetchDataRentingByClient();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    data,
    dataItems,
    dataRenting,
    activate,
    editMachineDialogState
  ]);

  // Error notification
  useEffect(() => {
    if (error?.message) {
      toast.error(t("GENERIC.ERROR") + `: ${error.message}`);
    }
  }, [error, t]);

  // Event handlers - handleSelectMachine is no longer used as rentals are now stopped through the return guide process
  const handleSelectMachine = (machine) => {
    // This function is kept for reference but is no longer used
    setSelectedMachines((prevSelected) => {
      if (prevSelected.includes(machine)) {
        return prevSelected.filter((m) => m !== machine);
      } else {
        return [...prevSelected, machine];
      }
    });
  };

  // This function is no longer used as rentals are now stopped through the return guide process
  const handleDelete = () => {
    // Kept for reference but no longer used
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClickOpen1 = (index, items) => {
    let arrayForSplice = [...items];
    let spliced = arrayForSplice.splice(index, 1);
    setNewItemsWOEliminated(arrayForSplice);
    setOpen1(true);
  };

  const handleClose1 = () => {
    setOpen1(false);
  };

  const handleItemSelection = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleAddItemsToMachine = () => {
    if (selectedItems.length === 0) {
      toast.warning(t("MACHINE_DETAIL.NO_ITEM_SELECTED"));
      return;
    }

    setLoadingAction(true);
    updateMachineItems({
      variables: {
        id,
        items: selectedItems,
      },
      })
      .then(() => {
        toast.success(t("MACHINE_DETAIL.SUCCESS_ADD_ITEMS"));
        setItemsPopupOpen(false);
        setSelectedItems([]);
        refetch();
        refetchItems();
      })
      .catch(error => {
        toast.error(t("MACHINE_DETAIL.ERROR_ADD_ITEMS", { message: error.message }));
      })
      .finally(() => {
        setLoadingAction(false);
      });
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const newDoc = {
          file,
          name: file?.name,
          flaggedForExpiration: false,
          expirationDate: null
        };
        setUploadedDocs((prevDocs) => [...prevDocs, newDoc]);
        toast.success(t("MACHINE_DETAIL.SUCCESS_LOAD_DOCUMENT", { name: file?.name }));
      } catch (error) {
        toast.error(t("MACHINE_DETAIL.ERROR_LOAD_DOCUMENT", { message: error.message }));
      }
    }
    event.target.value = '';
  };

  const handleAddDocumentsToMachine = async () => {
    if (uploadedDocs?.length) {
      setLoadingAction(true);
      try {
        const documents = await uploadMachineDocuments(uploadedDocs);
        await updateDocumentItems({
        variables: {
          input: {
            id: id,
            documents,
          },
        },
        });
        toast.success(t("MACHINE_DETAIL.SUCCESS_ADD_DOCUMENTS"));
        setDocumentsPopupOpen(false);
        setUploadedDocs([]);
        refetch();
      } catch (error) {
        toast.error(t("MACHINE_DETAIL.ERROR_ADD_DOCUMENTS", { message: error.message }));
      } finally {
        setLoadingAction(false);
      }
    } else {
      toast.warning(t("MACHINE_DETAIL.NO_DOCUMENT_UPLOADED"));
    }
  };

  const handleCloseFnNo = () => {
    setLoadingAction(true);
    removeItem({
      variables: { id, status: "NOT_REUSABLE", items: newItemsWOEliminated },
      })
      .then(() => {
        toast.success(t("MACHINE_DETAIL.SUCCESS_REMOVE_ITEM_NOT_REUSABLE"));
        setOpen1(false);
        refetch();
        refetchItems();
      })
      .catch(error => {
        toast.error(t("MACHINE_DETAIL.ERROR_REMOVE_ITEM", { message: error.message }));
      })
      .finally(() => {
        setLoadingAction(false);
      });
  };

  const handleCloseFnYes = () => {
    setLoadingAction(true);
    removeItem({
      variables: { id, status: "REUSABLE", items: newItemsWOEliminated },
      })
      .then(() => {
        toast.success(t("MACHINE_DETAIL.SUCCESS_REMOVE_ITEM_REUSABLE"));
        setOpen1(false);
        refetch();
        refetchItems();
      })
      .catch(error => {
        toast.error(t("MACHINE_DETAIL.ERROR_REMOVE_ITEM", { message: error.message }));
      })
      .finally(() => {
        setLoadingAction(false);
      });
  };

  const generateAndDownloadQRCode = async () => {
    try {
      setLoadingAction(true);
      const publicMachineUrl = new URL(`/public/${id}`, window.location.origin).toString();

      const canvas = document.createElement("canvas");
      await QRCode.toCanvas(canvas, publicMachineUrl, {
        width: 200,
        errorCorrectionLevel: "H",
      });

      const pngUrl = canvas.toDataURL("image/png");

      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = `${data?.machine?.name || id}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      toast.success(t("MACHINE_DETAIL.SUCCESS_QR"));
    } catch (error) {
      toast.error(t("MACHINE_DETAIL.ERROR_QR", { message: error.message }));
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteMachine = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    setLoadingAction(true);
    removeMachine({ variables: { id } })
      .then(response => {
        if (response?.data?.removeMachine) {
          toast.success(t("MACHINE_DETAIL.SUCCESS_REMOVE_MACHINE"));
          setConfirmDeleteOpen(false);
          navigate("/machines");
        } else {
          toast.error(t("MACHINE_DETAIL.ERROR_REMOVE_MACHINE_FAILED"));
        }
      })
      .catch(error => {
        toast.error(t("MACHINE_DETAIL.ERROR_REMOVE_MACHINE", { message: error.message }));
      })
      .finally(() => {
        setLoadingAction(false);
      });
  };

  // This function is used to cancel a rental (not stop it)
  const handleCancelRent = () => {
    setLoadingAction(true);
    cancelRent({ variables: { id } })
      .then(response => {
        if (response?.data?.cancelRenting) {
          toast.success(t("MACHINE_DETAIL.SUCCESS_CANCEL_RENT"));
          setDialogCancelRent(false);
          refetchRenting();
          refetchDataRentingByClient();
          refetch();
          refetchItems();
        } else {
          toast.error(t("MACHINE_DETAIL.ERROR_CANCEL_RENT_FAILED"));
        }
      })
      .catch(error => {
        toast.error(t("MACHINE_DETAIL.ERROR_CANCEL_RENT", { message: error.message }));
      })
      .finally(() => {
        setLoadingAction(false);
      });
  };

  const handleEditIntervention = (intervention) => {
    setCurrentIntervention(intervention);
    setDescription(intervention.obs || "");
    setPhotos(intervention.photosTaken || []);
    setOpenEditDialog(true);
  };

  const generateReport = () => {
    if (selectedInterventions.length === 0) {
      toast.warning(t("MACHINE_DETAIL.SELECT_INTERVENTION"));
      return;
    }

    if (selectedFields.length === 0) {
      toast.warning(t("MACHINE_DETAIL.SELECT_REPORT_FIELD"));
      return;
    }

    try {
      const reportData = selectedInterventions.map((intervention) => {
        const result = {};
        if (selectedFields.includes('Técnico')) result[t("COMMON.ROLE_TECHNICIAN")] = intervention.technicianInfo?.name;
        if (selectedFields.includes('Data Início')) result[t("FIELDS.startDate")] = toReadableDate(intervention.createdAt);
        if (selectedFields.includes('Tipo'))
          result[t("FIELDS.type")] = intervention.type === 'REPAIR' ? t("MOBILE_MACHINE.REPAIR") : t("MOBILE_MACHINE.MAINTENANCE");
        if (selectedFields.includes('Observações')) result[t("FIELDS.observations")] = intervention.obs;
        if (selectedFields.includes('Fotos')) result[t("FIELDS.photos")] = intervention.photosTaken;

        return result;
      });

      PDFGeneratorServiceFn.generatePDF(
        reportData,
        t("MACHINE_DETAIL.REPORT_TITLE", { name: data?.machine?.name || t("FIELDS.machine") })
      );
      setOpenReportDialog(false);
      toast.success(t("MACHINE_DETAIL.SUCCESS_REPORT"));
    } catch (error) {
      toast.error(t("MACHINE_DETAIL.ERROR_REPORT", { message: error.message }));
    }
  };

  const handleDeletePhoto = (idx) => {
    const updatedPhotos = [...photos];
    const [removedPhoto] = updatedPhotos.splice(idx, 1);
    if (removedPhoto?.preview) URL.revokeObjectURL(removedPhoto.preview);
    setPhotos(updatedPhotos);
  };

  const handlePhotoUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      setPhotos((prevPhotos) => [
        ...prevPhotos,
        ...Array.from(files).map(pendingInterventionPhoto),
      ]);
    }
    event.target.value = '';
  };

  const handleSaveChanges = async () => {
    setLoadingAction(true);
    try {
      const photoUrls = await uploadInterventionPhotos(photos.map(interventionPhotoValue));
      const updatedIntervention = {
        machineId: currentIntervention.machineId,
        obs: description,
        photosTaken: photoUrls,
        interventionId: currentIntervention._id,
      };

      await editIntervention({
        variables: {
          input: updatedIntervention,
        },
      });

      toast.success(t("MACHINE_DETAIL.SUCCESS_UPDATE_INTERVENTION"));
      setOpenEditDialog(false);
      refetchIntervention();
    } catch (error) {
      toast.error(t("MACHINE_DETAIL.ERROR_UPDATE_INTERVENTION", { message: error.message }));
    } finally {
      setLoadingAction(false);
    }
  };

  const fields = getFields("machines");
  // Global loading toast effect
  useEffect(() => {
    if (loadingAction) {
      toast.loading(t("COMMON.LOADING"), { toastId: "globalLoading" });
    } else {
      toast.dismiss("globalLoading");
    }
  }, [loadingAction, t]);
  // Render loading state
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: alpha(theme.palette.background.default, 0.7)
      }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ mt: 3, color: theme.palette.text.secondary }}>
          {t("MACHINE_DETAIL.LOADING_MACHINE_INFO")}
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
        p: 3
      }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{
            mb: 3,
            borderRadius: 2,
            width: '100%',
            maxWidth: 600,
            '& .MuiAlert-icon': { fontSize: 28 }
          }}
        >
          <Typography variant="h6">{t("ERROR.LOAD_MACHINE")}</Typography>
          <Typography variant="body2">{error.message}</Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/machines')}
          sx={{
            py: 1.5,
            px: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          }}
        >
          {t("MACHINE_DETAIL.BACK_TO_MACHINES")}
        </Button>
      </Box>
    );
  }

  const activeMachine = dataRenting?.renting?.status === 'ALREADY_RENTED';
  const machineDetails = data?.machine;

  const handleUpdateEndDate = async () => {
    if (!newEndDate) {
      toast.error(t("MACHINE_DETAIL.SELECT_NEW_DATE"));
      return;
    }

    setLoadingAction(true);
	    try {
	      await updateRentingEndDate({
	        variables: {
	          rentingId: dataRenting?.renting?._id,
	          newEndDate: new Date(newEndDate).toISOString()
	        }
	      });

      toast.success(t("MACHINE_DETAIL.SUCCESS_UPDATE_DATE"));
      setUpdateEndDateDialogOpen(false);
      setNewEndDate(null);
      refetchRenting();
      refetchDataRentingByClient();
    } catch (error) {
      switch (error.message) {
        case 'RENTING.NOT_FOUND':
          toast.error(t("MACHINE_DETAIL.ERROR_RENTING_NOT_FOUND"));
          break;

        case 'RENTING.NOT_ACTIVE':
          toast.error(t("MACHINE_DETAIL.ERROR_RENTING_NOT_ACTIVE"));
          break;

        case 'RENTING.END_DATE_BEFORE_START':
          toast.error(t("MACHINE_DETAIL.ERROR_END_BEFORE_START"));
          break;

        case 'RENTING.END_DATE_IN_PAST':
          toast.error(t("MACHINE_DETAIL.ERROR_END_IN_PAST"));
          break;

        default:
          toast.error(t("MACHINE_DETAIL.ERROR_UPDATE_DATE", { message: error.message }));
      }
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <>
      <Box sx={{
        backgroundColor: alpha(theme.palette.background.default, 0.5),
        minHeight: '100vh',
        pt: 3,
        pb: 8
      }}>
        {/* Header Section */}
        <Box sx={{ px: 4, mb: 4 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton
                  onClick={() => navigate('/machines')}
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.9),
                    boxShadow: theme.shadows[2],
                    '&:hover': { backgroundColor: theme.palette.common.white },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    textShadow: '0px 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  {machineDetails?.name || t("MACHINE_DETAIL.TITLE_FALLBACK")}
                </Typography>
                {machineDetails?.serialNumber && (
                  <Chip
                    label={`SN: ${machineDetails.serialNumber}`}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      fontWeight: 500,
                      borderRadius: 1.5,
                      '& .MuiChip-label': { px: 1.5 }
                    }}
                  />
                )}
                {machineDetails?.ref && (
                  <Chip
                    label={`REF: ${machineDetails.ref}`}
                    sx={{
                      backgroundColor: alpha(theme.palette.secondary.light, 0.1),
                      fontWeight: 500,
                      borderRadius: 1.5,
                      '& .MuiChip-label': { px: 1.5 }
                    }}
                  />
                )}
              </Stack>

              {machineDetails?.category && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    ml: 6
                  }}
                >
                  {t("MACHINE_DETAIL.CATEGORY")} <Box component="span" sx={{ color: theme.palette.text.primary }}>{machineDetails.category}</Box>
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Tooltip title={t("MACHINE_DETAIL.EDIT_MACHINE")} arrow>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CreateIcon />}
                    onClick={() => setEditMachineDialogState(true)}
                    sx={{
                      borderRadius: 2,
                      py: 1.25,
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      borderWidth: 1.5,
                      '&:hover': {
                        borderWidth: 1.5,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    {t("ACTIONS.EDIT")}
                  </Button>
                </Tooltip>

                <Tooltip title={t("MACHINE_DETAIL.DOWNLOAD_QR")} arrow>
                  <Button
                    variant="outlined"
                    color="secondary" startIcon={<QrCodeIcon />}
                    onClick={generateAndDownloadQRCode}
                    sx={{
                      borderRadius: 2,
                      py: 1.25,
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      borderWidth: 1.5,
                      '&:hover': {
                        borderWidth: 1.5,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    QR Code
                  </Button>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Main Content */}
        <Box sx={{ px: 4 }}>
          <Grid container spacing={4}>
            {/* Left Column - Machine Info */}
            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                {/* Machine Card */}
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  {/* Status Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      zIndex: 10,
                    }}
                  >
                    <Chip
                      icon={activeMachine ? <PlayArrowIcon /> : <StopIcon />}
                      label={activeMachine ? t("STATUS.IN_USE") : t("STATUS.AVAILABLE")}
                      color={activeMachine ? 'primary' : 'success'}
                      variant={activeMachine ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '& .MuiChip-icon': { fontSize: 16 },
                      }}
                    />
                  </Box>

                  {/* Machine Image */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 240,
                      backgroundColor: '#f0f2f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {machineDetails?.photo ? (
                      <Box
                        component="img"
                        src={machineDetails.photo}
                        alt={machineDetails?.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: 3,
                          transition: 'transform 0.6s ease',
                          '&:hover': {
                            transform: 'scale(1.04)',
                          },
                        }}
                      />
                    ) : (
                      <InventoryIcon
                        sx={{
                          fontSize: 100,
                          color: alpha(theme.palette.text.secondary, 0.3),
                        }}
                      />
                    )}

                    {/* Decorative pattern overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.04,
                        backgroundImage: `radial-gradient(${theme.palette.primary.main} 2px, transparent 2px)`,
                        backgroundSize: '30px 30px',
                        zIndex: 1,
                      }}
                    />
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    {/* Price per day */}
                    {machineDetails?.pricePerDay && (
                      <Box sx={{ mb: 3 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.success.light, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          }}
                        >
                          <EuroIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            {t("MACHINE_DETAIL.PRICE_PER_DAY")}
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              ml: 'auto',
                              fontWeight: 700,
                              color: theme.palette.success.dark,
                            }}
                          >
                            {machineDetails.pricePerDay}€
                          </Typography>
                        </Stack>
                      </Box>
                    )}

                    {/* Machine PIN */}
                    {dataRenting?.renting?.status === 'ALREADY_RENTED' && machineDetails?.pin && (
                      <Box sx={{ mb: 3 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.info.light, 0.1),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          }}
                        >
                          <WebIcon sx={{ color: theme.palette.info.main, mr: 1 }} />
                          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            PIN:
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              ml: 'auto',
                              fontWeight: 700,
                              color: theme.palette.info.dark,
                              letterSpacing: 1,
                            }}
                          >
                            {machineDetails.pin}
                          </Typography>
                        </Stack>
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Stack spacing={2}>
                      {dataRenting?.renting?.status !== 'ALREADY_RENTED' ? (
                        <Button
                          variant="contained"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => setIsOpenRent(true)}
                          sx={{
                            py: 1.75,
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          {t("MACHINE_DETAIL.RENT_MACHINE")}
                        </Button>
                      ) : (
                        <>
                          {/* "Parar Aluguer" button removed - rentals are now stopped through the return guide process */}

                          <Button
                            variant="outlined"
                            startIcon={<UpdateIcon />}
                            onClick={() => setUpdateEndDateDialogOpen(true)}
                            sx={{
                              py: 1.75,
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                              color: theme.palette.info.main,
                              '&:hover': {
                                borderWidth: 2,
                                borderColor: theme.palette.info.dark,
                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                              },
                            }}
                          >
                            {t("MACHINE_DETAIL.UPDATE_EXPECTED_DATE")}
                          </Button>

                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={() => setDialogCancelRent(true)}
                            sx={{
                              py: 1.75,
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              borderWidth: 2,
                              borderColor: theme.palette.secondary.main,
                              color: theme.palette.secondary.main,
                              '&:hover': {
                                borderWidth: 2,
                                borderColor: theme.palette.secondary.dark,
                                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              },
                            }}
                          >
                            {t("MACHINE_DETAIL.CANCEL_RENT")}
                          </Button>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Rental Information Card */}
                {dataRenting?.renting && (
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 3,
                          display: 'flex',
                          alignItems: 'center',
                          '&::before': {
                            content: '""',
                            display: 'block',
                            width: 4,
                            height: 24,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: 4,
                            mr: 2,
                          }
                        }}
                      >
                        {t("MACHINE_DETAIL.RENTING_INFO")}
                      </Typography>

                      {dataRenting.renting.status === 'ALREADY_RENTED' ? (
                        <Stack spacing={2.5}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {t("MACHINE_DETAIL.EXPECTED_END")}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <CalendarTodayIcon color="primary" sx={{ fontSize: 18 }} />
                              <Typography variant="body1" fontWeight={500}>
                                {toReadableDate(dataRenting.renting.previsionEndingRent)}
                              </Typography>
                            </Stack>
                          </Box>

                          <Divider />

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {t("MACHINE_DETAIL.PROJECTED_REVENUE")}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <TrendingUpIcon color="success" sx={{ fontSize: 18 }} />
                              <Typography variant="body1" fontWeight={600} color="success.main">
                                {calculateProjectedIncome(dataRenting)}€
                              </Typography>
                            </Stack>
                          </Box>

                          <Divider />

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {t("MACHINE_DETAIL.CURRENT_REVENUE")}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <EuroIcon color="success" sx={{ fontSize: 18 }} />
                              <Typography variant="h6" fontWeight={700} color="success.dark">
                                {calculateLiveIncome(dataRenting)}€
                              </Typography>
                            </Stack>
                          </Box>

                          {dataRenting.renting.client && dataRentingByClient?.rentingsByUser && (
                            <>
                              <Divider />
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {t("MACHINE_DETAIL.CLIENT")}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: theme.palette.primary.main,
                                    fontSize: 14,
                                  }}>
                                    {clientData?.getUser?.name?.charAt(0) || 'C'}
                                  </Avatar>
                                  <Typography variant="body1" fontWeight={500}>
                                    {clientData?.getUser?.name || t("MACHINE_DETAIL.CLIENT_FALLBACK")}
                                  </Typography>
                                </Stack>
                              </Box>
                            </>
                          )}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            py: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.success.light, 0.1),
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body1" color="success.main" fontWeight={500}>
                            {t("MACHINE_DETAIL.AVAILABLE_TO_RENT")}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>

            {/* Right Column - Tabs & Content */}
            <Grid item xs={12} md={8}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(10px)',
                }}
              >
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        py: 2,
                        px: 3,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                      },
                      '& .Mui-selected': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                      },
                    }}
                  >
                    <Tab
                      icon={<InventoryIcon />}
                      iconPosition="start"
                      label={t("MACHINE_DETAIL.ITEMS_TAB")}
                    />
                    <Tab
                      icon={<BuildIcon />}
                      iconPosition="start"
                      label={t("MACHINE_DETAIL.INTERVENTIONS_TAB")}
                    />
                    <Tab
                      icon={<DescriptionIcon />}
                      iconPosition="start"
                      label={t("MACHINE_DETAIL.DOCUMENTS_TAB")}
                    />
                  </Tabs>
                </Box>

                {/* Tab Panels */}
                <Box sx={{ p: 3 }}>
                  {/* Items Tab */}
                  {currentTab === 0 && (
                    <Box>
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {t("MACHINE_DETAIL.MACHINE_ITEMS")}
                        </Typography>
                        {dataRenting?.renting?.status !== 'ALREADY_RENTED' && (
                          <Button
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => setItemsPopupOpen(true)}
                            sx={{
                              borderRadius: 2,
                              py: 1.25,
                              px: 2.5,
                              backgroundColor: theme.palette.success.main,
                              '&:hover': {
                                backgroundColor: theme.palette.success.dark,
                              }
                            }}
                          >
                            {t("MACHINE_DETAIL.ADD_ITEMS")}
                          </Button>
                        )}
                      </Box>

                      {machineDetails?.items?.length ? (
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            overflow: 'hidden',
                            '& .MuiTableRow-root:last-child .MuiTableCell-body': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Table>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.light, 0.05) }}>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.name")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.brand")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.ref")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.stock")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.serialNumbers")}</TableCell>
                                {dataRenting?.renting?.status !== 'ALREADY_RENTED' && (
                                  <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.actions")}</TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {machineDetails.itemsInfo?.map((item, index) => (
                                <TableRow
                                  key={item._id}
                                  sx={{
                                    transition: 'background-color 0.2s',
                                    '&:nth-of-type(even)': { backgroundColor: alpha(theme.palette.primary.light, 0.02) },
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05) },
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 500 }}>{item?.name}</TableCell>
                                  <TableCell>{item.brand}</TableCell>
                                  <TableCell>{item.ref}</TableCell>
                                  <TableCell>{item.count}</TableCell>
                                  <TableCell>-</TableCell>
                                  {dataRenting?.renting?.status !== 'ALREADY_RENTED' && (
                                    <TableCell>
                                      <Tooltip title={t("MACHINE_DETAIL.REMOVE_ITEM")} arrow>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          startIcon={<DeleteIcon />}
                                          onClick={() => handleClickOpen1(index, machineDetails.items)}
                                          sx={{
                                            borderRadius: 1.5,
                                            textTransform: 'none',
                                            borderWidth: 1.5,
                                            px: 1.5,
                                            py: 0.75,
                                            '&:hover': {
                                              borderWidth: 1.5,
                                              backgroundColor: alpha(theme.palette.error.main, 0.05),
                                            },
                                          }}
                                        >
                                          {t("ACTIONS.DELETE")}
                                        </Button>
                                      </Tooltip>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box
                          sx={{
                            py: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                            borderRadius: 2,
                            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                          }}
                        >
                          <InventoryIcon
                            sx={{
                              fontSize: 60,
                              color: alpha(theme.palette.text.secondary, 0.3),
                              mb: 2,
                            }}
                          />
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            {t("MACHINE_DETAIL.NO_ITEMS")}
                          </Typography>
                          {dataRenting?.renting?.status !== 'ALREADY_RENTED' && (
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={() => setItemsPopupOpen(true)}
                              sx={{ mt: 2, borderRadius: 2 }}
                            >
                              {t("ACTIONS.ADD_FIRST_ITEM")}
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Interventions Tab */}
                  {currentTab === 1 && (
                    <Box>
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {t("MACHINE_DETAIL.INTERVENTIONS_HISTORY")}
                        </Typography>
                        {dataInterventions?.interventionsByMachine?.length > 0 && (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => setOpenReportDialog(true)}
                            sx={{
                              borderRadius: 2,
                              py: 1.25,
                              px: 2.5,
                              borderWidth: 1.5,
                              '&:hover': {
                                borderWidth: 1.5,
                              }
                            }}
                          >
                            {t("MACHINE_DETAIL.GENERATE_REPORT")}
                          </Button>
                        )}
                      </Box>

                      {loadingInterventions ? (
                        <Box sx={{ p: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : dataInterventions?.interventionsByMachine?.length > 0 ? (
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            overflow: 'hidden',
                            '& .MuiTableRow-root:last-child .MuiTableCell-body': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Table>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.light, 0.05) }}>
                                <TableCell sx={{ fontWeight: 600 }}>{t("COMMON.ROLE_TECHNICIAN")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.date")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.type")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.observations")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.photos")}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t("FIELDS.actions")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dataInterventions.interventionsByMachine.map((intervention) => (
                                <TableRow
                                  key={intervention._id}
                                  sx={{
                                    transition: 'background-color 0.2s',
                                    '&:nth-of-type(even)': { backgroundColor: alpha(theme.palette.primary.light, 0.02) },
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05) },
                                  }}
                                >
                                  <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Avatar
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          bgcolor: intervention.type === 'REPAIR'
                                            ? theme.palette.error.light
                                            : theme.palette.info.light,
                                          fontSize: 14,
                                        }}
                                      >
                                        {intervention.technicianInfo?.name.charAt(0)}
                                      </Avatar>
                                      <Typography fontWeight={500}>
                                        {intervention.technicianInfo?.name}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{toReadableDate(intervention.createdAt)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={intervention.type === 'REPAIR' ? t("MOBILE_MACHINE.REPAIR") : t("MOBILE_MACHINE.MAINTENANCE")}
                                      color={intervention.type === 'REPAIR' ? 'error' : 'info'}
                                      variant="outlined"
                                      size="small"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        maxWidth: 180,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {intervention.obs || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5}>
                                      {intervention.photosTaken.slice(0, 3).map((photo, idx) => (
                                        <Badge
                                          key={idx}
                                          color="primary"
                                          badgeContent={idx === 2 && intervention.photosTaken.length > 3 ? `+${intervention.photosTaken.length - 3}` : 0}
                                          overlap="circular"
                                        >
                                          <Avatar
                                            src={photo}
                                            variant="rounded"
                                            sx={{
                                              width: 40,
                                              height: 40,
                                              cursor: 'pointer',
                                              transition: 'transform 0.2s',
                                              '&:hover': {
                                                transform: 'scale(1.1)',
                                              },
                                            }}
                                            onClick={() => {
                                              setCurrentImage(photo);
                                              setOpenImageDialog(true);
                                            }}
                                          />
                                        </Badge>
                                      ))}
                                      {intervention.photosTaken.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                          {t("MACHINE_DETAIL.NO_PHOTOS")}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      startIcon={<CreateIcon />}
                                      onClick={() => handleEditIntervention(intervention)}
                                      sx={{
                                        borderRadius: 1.5,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        px: 1.5,
                                        py: 0.75,
                                        '&:hover': {
                                          borderWidth: 1.5,
                                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                        },
                                      }}
                                    >
                                      {t("ACTIONS.EDIT")}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box
                          sx={{
                            py: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                            borderRadius: 2,
                            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                          }}
                        >
                          <BuildIcon
                            sx={{
                              fontSize: 60,
                              color: alpha(theme.palette.text.secondary, 0.3),
                              mb: 2,
                            }}
                          />
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            {t("INTERVENTIONS.NO_INTERVENTIONS")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Documents Tab */}
                  {currentTab === 2 && (
                    <Box>
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {t("MACHINE_DETAIL.MACHINE_DOCUMENTS")}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AttachFileIcon />}
                          onClick={() => setDocumentsPopupOpen(true)}
                          sx={{
                            borderRadius: 2,
                            py: 1.25,
                            px: 2.5,
                            backgroundColor: theme.palette.secondary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.secondary.dark,
                            }
                          }}
                        >
                          {t("MACHINE_DETAIL.ADD_DOCUMENTS")}
                        </Button>
                      </Box>

                      {machineDetails?.documentsInfo?.length ? (
                        <Grid container spacing={2}>
                          {machineDetails.documentsInfo
                            .filter(doc => doc) // Filter out null entries
                            .map((document, idx) => (
                              <Grid item xs={12} md={6} key={idx}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[4] },
                                    overflow: 'hidden',
                                  }}
                                >
                                  {/* Document Preview */}
                                  <Box
                                    sx={{
                                      height: 140,
                                      backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                      position: 'relative',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <iframe
                                      src={convertBase64ToBlobUrl(document?.data)}
                                      width="100%"
                                      height="100%"
                                      style={{ border: 'none' }}
                                      title={document?.name || t("MACHINE_DETAIL.DOCUMENT_PREVIEW")}
                                    />
                                    {/* Visibility Badge */}
                                    <Chip
                                      icon={document?.public ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                      label={document?.public ? t("MACHINE_DETAIL.PUBLIC") : t("MACHINE_DETAIL.PRIVATE")}
                                      color={document?.public ? 'success' : 'default'}
                                      size="small"
                                      sx={{ position: 'absolute', top: 8, right: 8, fontWeight: 500 }}
                                    />
                                  </Box>

                                  <Box sx={{ p: 2, flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap>
                                      {document?.name || t("MACHINE_DETAIL.DOCUMENT_NUMBER", { number: idx + 1 })}
                                    </Typography>

                                    {/* Expiration Check */}
                                    {document.expirationDate && (() => {
                                      const expDate = new Date(Number(document.expirationDate))
                                      const now = new Date();
                                      // Calculate full days remaining
                                      const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
                                      // Determine the color: error if expired, warning if nearly expired, otherwise default text color
                                      const color = daysLeft < 0
                                        ? 'error.main'
                                        : daysLeft <= 2
                                          ? 'warning.main'
                                          : 'text.secondary';
                                      // Determine the label based on days left
                                      const label = daysLeft < 0
                                        ? t("MACHINE_DETAIL.EXPIRED_AT", { date: toReadableDate(Number(document.expirationDate)) })
                                        : daysLeft <= 2
                                          ? t("MACHINE_DETAIL.EXPIRES_IN_DAYS", { days: daysLeft })
                                          : t("MACHINE_DETAIL.EXPIRES_AT", { date: toReadableDate(Number(document.expirationDate)) });

                                      return (
                                        <Typography
                                          variant="caption"
                                          color={color}
                                          display="flex"
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                          {label}
                                        </Typography>
                                      );
                                    })()}
                                  </Box>

                                  <Divider />

                                  <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                                    <Tooltip title={t("MACHINE_DETAIL.VIEW_DOCUMENT")} arrow>
                                      <IconButton
                                        color="primary"
                                        onClick={() => {
                                          setCurrentDoc(document?.data);
                                          setOpenDocDialog(true);
                                        }}
                                      >
                                        <VisibilityIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title={document?.public ? t("MACHINE_DETAIL.MAKE_PRIVATE") : t("MACHINE_DETAIL.MAKE_PUBLIC")} arrow>
                                      <IconButton
                                        color={document?.public ? 'default' : 'success'}
                                        onClick={() => {
                                          setLoadingAction(true);
                                          updateDocumentPublic({
                                            variables: { id: machineDetails.documents[idx] },
                                            refetchQueries: [{ query: Machine, variables: { id } }],
                                            awaitRefetchQueries: true,
                                          })
                                            .then(() => toast.info(t("MACHINE_DETAIL.SUCCESS_UPDATE_DOCUMENT")))
                                            .catch(error => toast.error(t("GENERIC.ERROR") + `: ${error.message}`))
                                            .finally(() => { setLoadingAction(false); });
                                        }}
                                      >
                                        {document?.public ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title={t("MACHINE_DETAIL.DELETE_DOCUMENT")} arrow>
                                      <IconButton
                                        color="error"
                                        onClick={() => {
                                          setLoadingAction(true);
                                          updateRemoveDocumentItems({
                                            variables: { id, documentId: machineDetails.documents[idx] },
                                          })
                                            .then(() => {
                                              toast.info(t("MACHINE_DETAIL.SUCCESS_DELETE_DOCUMENT"));
                                              refetch();
                                            })
                                            .catch(error => toast.error(t("GENERIC.ERROR") + `: ${error.message}`))
                                            .finally(() => { setLoadingAction(false); });
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title={t("MACHINE_DETAIL.DOWNLOAD_DOCUMENT")} arrow>
                                      <IconButton
                                        color="primary"
                                        component="a"
                                        href={`${document?.data}`}
                                        download={document?.name || 'document.pdf'}
                                      >
                                        <DownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Card>
                              </Grid>
                            ))}
                        </Grid>
                      ) : (
                        <Box
                          sx={{
                            py: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                            borderRadius: 2,
                            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                          }}
                        >
                          <DescriptionIcon
                            sx={{
                              fontSize: 60,
                              color: alpha(theme.palette.text.secondary, 0.3),
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {t("MACHINE_DETAIL.NO_DOCUMENTS")}
                          </Typography>
                          <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<AttachFileIcon />}
                            onClick={() => setDocumentsPopupOpen(true)}
                            sx={{ mt: 2, borderRadius: 2 }}
                          >
                            {t("MACHINE_DETAIL.ADD_FIRST_DOCUMENT")}
                          </Button>
                        </Box>
                      )}

                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box >

      {/* Floating Action Button for Deleting Machine */}
      < Tooltip title={t("MACHINE_DETAIL.DELETE_MACHINE")} arrow >
        <Fab
          color="error"
          aria-label={t("ACTIONS.DELETE")}
          onClick={handleDeleteMachine}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            boxShadow: theme.shadows[6],
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'rotate(15deg)',
            },
          }}
        >
          <DeleteIcon />
        </Fab>
      </Tooltip >

      {/* ================ DIALOGS ================ */}

      {/* Edit Intervention Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BuildIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t("INTERVENTIONS.EDIT_TITLE")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <TextField
            label={t("FIELDS.description")}
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            {t("INTERVENTIONS.PHOTOS")}
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            {/* Existing Photos */}
            {photos.map((photo, idx) => (
              <Grid item key={idx} sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: theme.shadows[2],
                    '&:hover .photo-overlay': {
                      opacity: 1,
                    },
                  }}
                >
                  <img
                    src={interventionPhotoSource(photo)}
                    alt={`Photo ${idx}`}
                    width="100%"
                    height="100%"
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    className="photo-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        sx={{
                          color: 'white',
                          backgroundColor: alpha('#fff', 0.2),
                          '&:hover': {
                            backgroundColor: alpha('#fff', 0.3),
                          },
                        }}
                        onClick={() => {
                          setCurrentImage(interventionPhotoSource(photo));
                          setOpenImageDialog(true);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          color: 'white',
                          backgroundColor: alpha(theme.palette.error.main, 0.7),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.9),
                          },
                        }}
                        onClick={() => handleDeletePhoto(idx)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>
              </Grid>
            ))}

            {/* Add Photo Button */}
            <Grid item>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-photo"
                type="file"
                multiple
                onChange={handlePhotoUpload}
              />
              <label htmlFor="upload-photo">
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CameraIcon
                    color="primary"
                    sx={{ fontSize: 32, mb: 1, opacity: 0.7 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t("ACTIONS.ADD")}
                  </Typography>
                </Box>
              </label>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={() => setOpenEditDialog(false)}
            variant="outlined"
            color="inherit"
            startIcon={<CancelIcon />}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              mr: 1,
            }}
          >
            {t("ACTIONS.CANCEL")}
          </Button>
          <Button
            onClick={handleSaveChanges}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            disabled={loadingAction}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            {loadingAction ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("MACHINE_DETAIL.SAVE_CHANGES")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Rent Dialog */}
      <Dialog
        open={dialogCancelRent}
        onClose={() => setDialogCancelRent(false)}
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.error.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          color: theme.palette.error.main,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CancelIcon color="error" />
            <Typography variant="h6" fontWeight={600} color="error.main">
              {t("MACHINE_DETAIL.CANCEL_RENT")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
            <Typography variant="body1">
              {t("MACHINE_DETAIL.CANCEL_RENT_WARNING")}
            </Typography>
          </Alert>
          <Typography variant="body1">
            {t("MACHINE_DETAIL.CANCEL_RENT_CONFIRM")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={() => setDialogCancelRent(false)}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              mr: 1,
            }}
          >
            {t("ACTIONS.BACK")}
          </Button>
          <Button
            onClick={handleCancelRent}
            variant="contained"
            color="error"
            disabled={loadingAction}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            {loadingAction ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("MACHINE_DETAIL.CONFIRM_CANCEL")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Machine Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.error.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          color: theme.palette.error.main,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6" fontWeight={600} color="error.main">
              {t("MACHINE_DETAIL.DELETE_MACHINE")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Alert severity="error" variant="outlined" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={500}>
              {t("MACHINE_DETAIL.PERMANENT_ACTION")}
            </Typography>
          </Alert>
          <Typography variant="body1">
            {t("MACHINE_DETAIL.DELETE_MACHINE_CONFIRM")} <strong>{machineDetails?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            color="inherit"
            startIcon={<CancelIcon />}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              mr: 1,
            }}
          >
            {t("ACTIONS.CANCEL")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={loadingAction}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            {loadingAction ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("MACHINE_DETAIL.DELETE_PERMANENTLY")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reusable Item Dialog */}
      <Dialog
        open={open1}
        onClose={handleClose1}
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Typography variant="h6" fontWeight={600}>
            {t("MACHINE_DETAIL.ITEM_REUSABLE_TITLE")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography variant="body1">
            {t("MACHINE_DETAIL.ITEM_REUSABLE_DESC")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={handleCloseFnNo}
            variant="contained"
            color="error"
            disabled={loadingAction}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              mr: 1,
            }}
          >
            {t("MACHINE_DETAIL.NOT_REUSABLE")}
          </Button>
          <Button
            onClick={handleCloseFnYes}
            variant="contained"
            color="success"
            disabled={loadingAction}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            {t("MACHINE_DETAIL.REUSABLE")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Items Selection Dialog */}
      <Dialog
        open={itemsPopupOpen}
        onClose={() => {
          setItemsPopupOpen(false);
          setSelectedItems([]);
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(250,250,252,0.95))',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 80px rgba(0,0,0,0.1), 0 10px 30px rgba(0,0,0,0.08)',
          }
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        className="enhanced-dialog"
      >
        <DialogTitle
          sx={{
            py: 3,
            px: 4,
            background: `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: 0.03,
              backgroundImage: `radial-gradient(circle, ${theme.palette.primary.main} 2px, transparent 2px)`,
              backgroundSize: '20px 20px',
              zIndex: 0,
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  backgroundImage: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.01em',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <AddCircleOutlineIcon
                  sx={{
                    mr: 1.5,
                    fontSize: 28,
                    color: theme.palette.primary.main,
                  }}
                />
                {t("MACHINE_DETAIL.ADD_ITEMS_TO_MACHINE")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  ml: 5,
                  maxWidth: 500,
                }}
              >
                {t("MACHINE_DETAIL.ADD_ITEMS_DESC")}
              </Typography>
            </Box>

            <Badge
              badgeContent={selectedItems.length}
              color="primary"
              max={999}
              showZero
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.85rem',
                  height: '1.8rem',
                  minWidth: '1.8rem',
                  fontWeight: 600,
                  padding: '0 0.6rem',
                  borderRadius: '1rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }
              }}
            >
              <Chip

                label={t("MACHINE_DETAIL.SELECTED_ITEMS")}
                color={selectedItems.length > 0 ? "primary" : "default"}
                variant={selectedItems.length > 0 ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  height: 40,
                  borderRadius: 3,
                  px: 1,
                  backdropFilter: 'blur(4px)',
                  '& .MuiChip-icon': { ml: 1 },
                  ...(selectedItems.length > 0 && {
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                  })
                }}
              />
            </Badge>
          </Stack>
        </DialogTitle>

        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(8px)',
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
            px: 4,
            py: 2.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <TextField
            label={t("MACHINE_DETAIL.SEARCH_ITEMS")}
            placeholder="Ex: Martelo, Bosch, ABC-123..."
            fullWidth
            variant="outlined"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              refetchItems();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setSearchTerm('');
                      refetchItems();
                    }}
                    size="small"
                    sx={{ mr: -0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                '&.Mui-focused': {
                  bgcolor: 'background.paper',
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                },
                transition: 'all 0.2s',
                pr: searchTerm ? 0.5 : 2
              }
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.2),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
            }}
          />
        </Box>

        <DialogContent
          sx={{
            p: 4,
            pt: 3,
            height: '65vh',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(theme.palette.text.secondary, 0.2)} transparent`,
            '&::-webkit-scrollbar': {
              width: '8px',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.2),
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.3),
              },
            },
          }}
        >
          {loadingItems ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                py: 8,
              }}
            >
              <CircularProgress
                size={56}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  opacity: 0.8,
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mt: 3,
                  fontWeight: 500,
                  animation: 'pulse 2s infinite ease-in-out',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.6 },
                    '50%': { opacity: 1 },
                    '100%': { opacity: 0.6 },
                  },
                }}
              >
                {t("MACHINE_DETAIL.LOADING_ITEMS")}
              </Typography>
            </Box>
          ) : items.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              <Grid container spacing={2.5}>
                {items.map((item) => {
                  const isSelected = selectedItems.includes(item._id);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                      <Card
                        elevation={0}
                        onClick={() => handleItemSelection(item._id)}
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          border: `1px solid ${isSelected
                            ? theme.palette.primary.main
                            : alpha(theme.palette.divider, 0.1)
                            }`,
                          backgroundColor: isSelected
                            ? alpha(theme.palette.primary.light, 0.08)
                            : alpha(theme.palette.background.paper, 0.6),
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden',
                          ...(isSelected && {
                            boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                          }),
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: isSelected
                              ? `0 0 0 1px ${theme.palette.primary.main}, 0 12px 28px ${alpha(theme.palette.primary.main, 0.25)}`
                              : theme.shadows[8],
                            backgroundColor: isSelected
                              ? alpha(theme.palette.primary.light, 0.12)
                              : alpha(theme.palette.background.paper, 0.9),
                            borderColor: isSelected
                              ? theme.palette.primary.main
                              : alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        {/* Selection indicator */}
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -1,
                              right: -1,
                              width: 70,
                              height: 70,
                              background: `linear-gradient(135deg, transparent 50%, ${theme.palette.primary.main} 50%)`,
                              zIndex: 1,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{
                                position: 'absolute',
                                right: 7,
                                top: 7,
                                color: 'white',
                                fontSize: 24,
                                filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))',
                              }}
                            />
                          </Box>
                        )}

                        <CardContent
                          sx={{
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              mb: 2,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.2)
                                  : alpha(theme.palette.primary.light, 0.2),
                                color: theme.palette.primary.main,
                                borderRadius: 3,
                                mr: 2,
                                fontWeight: 700,
                                fontSize: '1.2rem',
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                              }}
                            >
                              {item?.name.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                fontWeight={600}
                                color="text.primary"
                                gutterBottom
                                sx={{
                                  lineHeight: 1.2,
                                  fontSize: '1.1rem',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {item?.name}
                              </Typography>

                              <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
                                {item.count !== undefined && (
                                  <Chip
                                    size="small"
                                    label={t("MACHINE_DETAIL.AVAILABLE_COUNT", { count: item.count })}
                                    color="success"
                                    variant="outlined"
                                    sx={{
                                      height: 24,
                                      '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1.5, opacity: 0.6 }} />

                          <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                            {item.brand && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <BusinessIcon
                                  sx={{
                                    color: alpha(theme.palette.text.secondary, 0.7),
                                    fontSize: 18
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  <Box component="span" sx={{ color: alpha(theme.palette.text.primary, 0.7) }}>
                                    {item.brand}
                                  </Box>
                                </Typography>
                              </Stack>
                            )}

                            {item.ref && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <QrCodeIcon
                                  sx={{
                                    color: alpha(theme.palette.text.secondary, 0.7),
                                    fontSize: 18
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {t("FIELDS.ref")}: <Box component="span" sx={{ color: alpha(theme.palette.text.primary, 0.7) }}>
                                    {item.ref}
                                  </Box>
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            <Box
              sx={{
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 3,
                border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                height: '100%',
                minHeight: 300,
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.error.light, 0.1),
                  color: theme.palette.error.main,
                  mb: 3,
                }}
              >
                <SearchOffIcon
                  sx={{
                    fontSize: 50,
                    opacity: 0.7,
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                {t("VIEWER.NO_ITEM_FOUND")}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 500,
                  textAlign: 'center',
                  mb: 3,
                  px: 2
                }}
              >
                {t("MACHINE_DETAIL.NO_SEARCH_ITEMS")}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setSearchTerm('');
                  refetchItems();
                }}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                }}
              >
                {t("MACHINE_DETAIL.CLEAR_SEARCH_RETRY")}
              </Button>
            </Box>
          )}
        </DialogContent>

        <Divider sx={{
          borderColor: alpha(theme.palette.divider, 0.1),
          borderWidth: selectedItems.length > 0 ? 0 : 1,
          mb: selectedItems.length > 0 ? 0 : -1
        }} />

        {selectedItems.length > 0 && (
          <Box
            sx={{
              px: 4,
              py: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.08),
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                  {t("MACHINE_DETAIL.SELECTED_ITEMS_COUNT", { count: selectedItems.length })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("MACHINE_DETAIL.SELECTED_ITEMS_HELP")}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setSelectedItems([])}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    borderColor: alpha(theme.palette.divider, 0.2),
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: alpha(theme.palette.divider, 0.5),
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  {t("MACHINE_DETAIL.CLEAR_SELECTION")}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    // Select all visible items
                    const allVisibleIds = items.map(item => item._id);
                    setSelectedItems(allVisibleIds);
                  }}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    backgroundColor: alpha(theme.palette.primary.main, 0.8),
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {t("MACHINE_DETAIL.SELECT_ALL")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        <DialogActions
          sx={{
            px: 4,
            py: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Button
            onClick={() => {
              setItemsPopupOpen(false);
              setSelectedItems([]);
            }}
            variant="outlined"
            color="inherit"
            startIcon={<CancelIcon />}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              mr: 2,
              fontWeight: 600,
              borderWidth: 1.5,
              borderColor: alpha(theme.palette.divider, 0.2),
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: alpha(theme.palette.divider, 0.4),
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
                borderWidth: 1.5,
              },
              transition: 'all 0.2s',
            }}
          >
            {t("ACTIONS.CANCEL")}
          </Button>

          <LoadingButton
            onClick={handleAddItemsToMachine}
            variant="contained"
            color="primary"
            disabled={selectedItems.length === 0}
            loading={loadingAction}
            loadingPosition="start"
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              backgroundImage: `linear-gradient(120deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.9)})`,
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                backgroundImage: `linear-gradient(120deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              },
              '&.Mui-disabled': {
                backgroundColor: alpha(theme.palette.action.disabledBackground, 0.8),
                color: theme.palette.action.disabled,
              },
              transition: 'all 0.3s',
            }}
          >
            {loadingAction
              ? t("MACHINE_DETAIL.ADDING")
              : t("MACHINE_DETAIL.ADD_SELECTED_ITEMS", { count: selectedItems.length })}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Document Uploader Dialog */}
      <Dialog
        open={documentsPopupOpen}
        onClose={() => {
          setDocumentsPopupOpen(false);
          setUploadedDocs([]);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.secondary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          color: theme.palette.secondary.main,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachFileIcon color="secondary" />
            <Typography variant="h6" fontWeight={600} color="secondary.main">
              {t("MACHINE_DETAIL.ADD_DOCUMENTS")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {/* Upload Area */}
          <Box
            sx={{
              p: 4,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              border: `2px dashed ${alpha(theme.palette.secondary.main, 0.3)}`,
            }}
          >
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleDocumentUpload}
              style={{ display: 'none' }}
              id="document-upload"
            />
            <label htmlFor="document-upload">
              <Button
                component="span"
                variant="outlined"
                color="secondary"
                startIcon={<DescriptionIcon />}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 500,
                  borderWidth: 1.5,
                  '&:hover': {
                    borderWidth: 1.5,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  },
                }}
              >
                {t("MACHINE_DETAIL.SELECT_DOCUMENT")}
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("MACHINE_DETAIL.DOCUMENT_TYPES_HELP")}
            </Typography>
          </Box>

          {/* Uploaded Documents Preview */}
          {uploadedDocs.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t("MACHINE_DETAIL.UPLOADED_DOCUMENTS", { count: uploadedDocs.length })}
              </Typography>
              <Grid container spacing={2}>
                {uploadedDocs.map((doc, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Document Preview */}
                      <Box sx={{ height: 120, position: 'relative', bgcolor: 'grey.100' }}>
                        <object
                          data={`${doc?.data}`}
                          type="application/pdf"
                          width="100%"
                          height="100%"
                          style={{ pointerEvents: 'none' }}
                        >
                          <Typography variant="caption" sx={{ p: 1 }}>
                            {t("MACHINE_DETAIL.PREVIEW_UNAVAILABLE")}
                          </Typography>
                        </object>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            p: 0.5,
                            backgroundColor: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0 0 0 0 8px',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newDocs = [...uploadedDocs];
                              newDocs.splice(index, 1);
                              setUploadedDocs(newDocs);
                            }}
                            sx={{
                              color: theme.palette.error.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" noWrap fontWeight={500}>
                          {doc?.name || t("MACHINE_DETAIL.DOCUMENT_NUMBER", { number: index + 1 })}
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={doc?.flaggedForExpiration}
                                onChange={() => {
                                  const newDocs = [...uploadedDocs];
                                  newDocs[index].flaggedForExpiration = !newDocs[index].flaggedForExpiration;
                                  if (!newDocs[index]?.flaggedForExpiration) {
                                    delete newDocs[index].expirationDate;
                                  }
                                  setUploadedDocs(newDocs);
                                }}
                                color="secondary"
                                size="small"
                              />
                            }
                            label={t("MACHINE_DETAIL.SET_EXPIRATION_DATE")}
                          />

                          {doc?.flaggedForExpiration && (
                            <TextField
                              type="date"
                              size="small"
                              fullWidth
                              value={doc.expirationDate || ''}
                              onChange={(e) => {
                                const newDocs = [...uploadedDocs];
                                newDocs[index].expirationDate = e.target.value;
                                setUploadedDocs(newDocs);
                              }}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={() => {
              setDocumentsPopupOpen(false);
              setUploadedDocs([]);
            }}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              mr: 1,
            }}
          >
            {t("ACTIONS.CANCEL")}
          </Button>
          <Button
            onClick={handleAddDocumentsToMachine}
            variant="contained"
            color="secondary"
            disabled={uploadedDocs.length === 0 || loadingAction}
            startIcon={uploadedDocs.length > 0 && !loadingAction && <AttachFileIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            {loadingAction ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("MACHINE_DETAIL.ADD_DOCUMENTS_COUNT", { count: uploadedDocs.length })
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PictureAsPdfIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t("MACHINE_DETAIL.GENERATE_INTERVENTION_REPORT")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            {/* Intervenção Selection */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t("MACHINE_DETAIL.SELECT_INTERVENTIONS")}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {dataInterventions?.interventionsByMachine?.map((intervention) => (
                  <FormControlLabel
                    key={intervention._id}
                    control={
                      <Checkbox
                        checked={selectedInterventions.includes(intervention)}
                        onChange={() =>
                          setSelectedInterventions((prev) =>
                            prev.includes(intervention)
                              ? prev.filter((i) => i !== intervention)
                              : [...prev, intervention]
                          )
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {intervention.type === 'REPAIR' ? t("MOBILE_MACHINE.REPAIR") : t("MOBILE_MACHINE.MAINTENANCE")} - {toReadableDate(intervention.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("COMMON.ROLE_TECHNICIAN")}: {intervention.technicianInfo?.name}
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', mb: 1 }}
                  />
                ))}

                {dataInterventions?.interventionsByMachine?.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    {t("INTERVENTIONS.NO_INTERVENTIONS")}
                  </Typography>
                )}
              </Paper>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => setSelectedInterventions(dataInterventions?.interventionsByMachine || [])}
                >
                  {t("MACHINE_DETAIL.SELECT_ALL_F")}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => setSelectedInterventions([])}
                >
                  {t("MACHINE_DETAIL.CLEAR_SELECTION")}
                </Button>
                <Chip
                  label={t("MACHINE_DETAIL.SELECTED_F", { count: selectedInterventions.length })}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Grid>

            {/* Fields Selection */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t("MACHINE_DETAIL.SELECT_REPORT_FIELDS")}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Grid container spacing={1}>
                  {[
                    { key: 'Técnico', label: t("COMMON.ROLE_TECHNICIAN") },
                    { key: 'Data Início', label: t("FIELDS.startDate") },
                    { key: 'Tipo', label: t("FIELDS.type") },
                    { key: 'Observações', label: t("FIELDS.observations") },
                    { key: 'Fotos', label: t("FIELDS.photos") },
                  ].map((field) => (
                    <Grid item xs={12} key={field.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFields.includes(field.key)}
                            onChange={() =>
                              setSelectedFields((prev) =>
                                prev.includes(field.key)
                                  ? prev.filter((f) => f !== field.key)
                                  : [...prev, field.key]
                              )
                            }
                            color="primary"
                          />
                        }
                        label={field.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => setSelectedFields(['Técnico', 'Data Início', 'Tipo', 'Observações', 'Fotos'])}
                >
                  {t("MACHINE_DETAIL.SELECT_ALL_M")}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => setSelectedFields([])}
                >
                  {t("MACHINE_DETAIL.CLEAR_SELECTION")}
                </Button>
                <Chip
                  label={t("MACHINE_DETAIL.SELECTED_FIELDS", { count: selectedFields.length })}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={() => setOpenReportDialog(false)}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              mr: 1,
            }}
          >
            {t("ACTIONS.CANCEL")}
          </Button>
          <Button
            onClick={generateReport}
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            disabled={selectedInterventions.length === 0 || selectedFields.length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            {t("MACHINE_DETAIL.GENERATE_REPORT")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop Rent Dialog removed - rentals are now stopped through the return guide process */}

      {/* Edit Machine Dialog */}
      <Dialog
        fullScreen
        open={editMachineDialogState}
        onClose={() => setEditMachineDialogState(false)}
        TransitionComponent={Slide}
        PaperProps={{
          sx: {
            backgroundColor: alpha(theme.palette.background.default, 0.97),
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setEditMachineDialogState(false)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              {t("MACHINE_DETAIL.EDIT_MACHINE")}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setLoadingAction(true);
                updateMachine({
                  variables: {
                    input: {
                      _id: id,
                      name: inputs?.name,
                      serialNumber: inputs.serialNumber,
                      ref: inputs.ref,
                      category: inputs.category,
                      photo: image || inputs,
                    },
                  },
                })
                  .then(() => {
                    toast.success(t("MACHINE_DETAIL.SUCCESS_UPDATE_MACHINE"));
                    setEditMachineDialogState(false);
                    refetch();
                  })
                  .catch(error => {
                    toast.error(t("MACHINE_DETAIL.ERROR_UPDATE_MACHINE", { message: error.message }));
                  })
                  .finally(() => {
                    setLoadingAction(false);
                  });
              }}
              disabled={loadingAction}
              startIcon={!loadingAction && <CheckCircleIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: theme.shadows[2],
              }}
            >
              {loadingAction ? <CircularProgress size={24} color="inherit" /> : t("MACHINE_DETAIL.SAVE_CHANGES")}
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="md" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                {t("MACHINE_DETAIL.MACHINE_IMAGE")}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <ImageUpload
                    cb={setImage}
                    defa={inputs}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  {t("MACHINE_DETAIL.IMAGE_UPLOAD_HELP")}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                {t("MACHINE_DETAIL.TITLE_FALLBACK")}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                }}
              >
                <Grid container spacing={3}>
                  {fields.map((f, i) => {
                    if (f.type === "image") {
                      return null; // Already handled in the left column
                    }

                    if (f.type === "string") {
                      return (
                        <Grid item xs={12} key={i}>
                          <TextField
                            label={t(`FIELDS.${f.fieldName}`, f.fieldName)}
                            variant="outlined"
                            fullWidth
                            value={inputs[f.fieldName] || ""}
                            onChange={(ev) => {
                              const old = inputs;
                              const newly = { ...old, [f.fieldName]: ev.target.value };
                              setInputs(newly);
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                      );
                    }

                    if (f.type === "select") {
                      return (
                        <Grid item xs={12} key={i}>
                          <TextField
                            select
                            label={t(`FIELDS.${f.fieldName}`, f.fieldName)}
                            fullWidth
                            value={inputs[f.fieldName] || ""}
                            onChange={(ev) => {
                              const old = inputs;
                              const newly = { ...old, [f.fieldName]: ev.target.value };
                              setInputs(newly);
                            }}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          >
                            {f.selectFields.map((field) => (
                              <MenuItem value={field} key={field}>
                                {field}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }

                    if (f.type === "multiple") {
                      return (
                        <Grid item xs={12} key={i}>
                          <Grid container spacing={2}>
                            {f.fields.map((subField, idx) => (
                              <Grid item xs={12} sm={6} key={idx}>
                                <TextField
                                  label={t(`FIELDS.${subField.fieldName}`, subField.fieldName)}
                                  fullWidth
                                  variant="outlined"
                                  value={inputs[subField.fieldName] || ""}
                                  onChange={(ev) => {
                                    const old = inputs;
                                    const newly = { ...old, [subField.fieldName]: ev.target.value };
                                    setInputs(newly);
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      );
                    }

                    return null;
                  })}
                </Grid>
              </Paper>

              {/* Price per day field (if not already in fields) */}
              {!fields.some(f =>
                (f.type === 'string' && f.fieldName === 'pricePerDay') ||
                (f.type === 'multiple' && f.fields.some(sf => sf.fieldName === 'pricePerDay'))
              ) && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mt: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      {t("MACHINE_DETAIL.PRICE_AND_AVAILABILITY")}
                    </Typography>
                    <TextField
                      label={t("FIELDS.pricePerDayEuro")}
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={inputs.pricePerDay || ""}
                      onChange={(ev) => {
                        const old = inputs;
                        const newly = { ...old, pricePerDay: ev.target.value };
                        setInputs(newly);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EuroIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Paper>
                )}
            </Grid>
          </Grid>
        </Container>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={openDocDialog}
        fullScreen
        onClose={() => setOpenDocDialog(false)}
        TransitionComponent={Slide}
      >
        <AppBar position="sticky" color="inherit" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setOpenDocDialog(false)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 500 }}>
              {t("MACHINE_DETAIL.DOCUMENT_VIEWER")}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              component="a"
              href={`${currentDoc}`}
              download="documento.pdf"
              sx={{ borderRadius: 2 }}
            >
              {t("MACHINE_DETAIL.DOWNLOAD")}
            </Button>
          </Toolbar>
        </AppBar>
        <Box width="100%" height="calc(100vh - 64px)">
          <object
            data={convertBase64ToBlobUrl(currentDoc)}
            type="application/pdf"
            width="100%"
            height="100%"
          >
            <Box
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" gutterBottom>
                {t("MACHINE_DETAIL.DOCUMENT_VIEW_FAILED")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href={`${currentDoc}`}
                download="documento.pdf"
                startIcon={<DownloadIcon />}
                sx={{ mt: 2 }}
              >
                {t("MACHINE_DETAIL.DOWNLOAD_DOCUMENT_ACTION")}
              </Button>
            </Box>
          </object>
        </Box>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'transparent',
          }
        }}
      >
        <IconButton
          onClick={() => setOpenImageDialog(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
            zIndex: 1,
          }}
        >
          <CancelIcon />
        </IconButton>
        <Box
          sx={{
            width: '100%',
            height: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.9)',
            p: 2,
          }}
        >
          <img
            src={currentImage}
            alt="Full size"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 8,
            }}
          />
        </Box>
      </Dialog>

      {/* Rent Popup */}
      <RentingPopup
        state={openRent}
        setState={setIsOpenRent}
        setActivate={setActivate}
        id={id}
        onRented={() => {
          refetchRenting();
          refetch();
        }}
      />

      {/* PDF Popup */}
      <Dialog
        open={pdfPopup}
        fullScreen
        onClose={() => setPdfPopup(false)}
        TransitionComponent={Slide}
      >
        <AppBar position="sticky" color="inherit" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setPdfPopup(false)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 500 }}>
              {t("MACHINE_DETAIL.RENT_CLOSE_REPORT")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              sx={{ borderRadius: 2 }}
            >
              {t("MACHINE_DETAIL.DOWNLOAD_PDF")}
            </Button>
          </Toolbar>
        </AppBar>
        <PDFGeneratorService data={PdfData} />
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingAction}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Update End Date Dialog */}
      <Dialog
        open={updateEndDateDialogOpen}
        onClose={() => setUpdateEndDateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          py: 2.5,
          bgcolor: alpha(theme.palette.info.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          color: theme.palette.info.main,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarTodayIcon color="info" />
            <Typography variant="h6" fontWeight={600} color="info.main">
              {t("MACHINE_DETAIL.UPDATE_EXPECTED_END_DATE")}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t("MACHINE_DETAIL.UPDATE_EXPECTED_END_HELP")}
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("FIELDS.startDate")}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {dataRenting?.renting?.startedRent ? new Date(dataRenting.renting.startedRent).toLocaleDateString() : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("MACHINE_DETAIL.CURRENT_EXPECTED_DATE")}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {dataRenting?.renting?.previsionEndingRent ? new Date(dataRenting.renting.previsionEndingRent).toLocaleDateString() : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("MACHINE_DETAIL.NEW_EXPECTED_DATE")}
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={newEndDate || ''}
                onChange={(e) => setNewEndDate(e.target.value)}
                inputProps={{
                  min: dataRenting?.renting?.startedRent ? new Date(dataRenting.renting.startedRent).toISOString().split('T')[0] : undefined,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={() => {
              setUpdateEndDateDialogOpen(false);
              setNewEndDate(null);
            }}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              mr: 1,
            }}
          >
            {t("ACTIONS.CANCEL")}
          </Button>
          <LoadingButton
            onClick={handleUpdateEndDate}
            variant="contained"
            color="info"
            loading={loadingAction}
            disabled={!newEndDate}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            {t("MACHINE_DETAIL.UPDATE_DATE")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
